import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import {
  ArrowLeft,
  CheckCircle2,
  Printer,
  Copy,
  Check,
  Plus,
  Trash2,
  AlertCircle,
  FileText,
  Send,
  X
} from 'lucide-react';
import './CustomerFormPage.css';

const CustomerFormPage = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Form State
  const [formData, setFormData] = useState({
    // Basic Information (1 - 16)
    name: '',
    fatherName: '',
    motherName: '',
    residenceAddress: '',
    officeAddress: '',
    occupation: '',
    monthlyIncome: '',
    department: '',
    pfTokenBatchNo: '',
    lengthOfService: '',
    dateOfBirth: '',
    placeOfBirth: '',
    age: '',
    educationalQualification: '',
    deformityIfAny: '',
    previousIllnessIfAny: '',

    // Agent and Policy Information
    agentName: 'Meenakshi Sundaram S',
    code: '0543571A',
    policyNo: '',

    // Bank Account Details
    accountType: 'SB A/c',
    accountNo: '',
    mcir9Digit: '',
    ifsCode: '',
    bankName: '',
    nameAndAddressOfBank: '',

    // Telephone Nos. (With STD Code)
    telOffice: '',
    telResidence: '',
    panCardNo: '',
    aadharNo: '',
    email: '',

    // FOR LADY LIFE (17)
    dtLastMenstruation: '',
    dtLastDelivery: '',
    husbandName: '',
    husbandMonthlyIncome: '',
    husbandInsuranceParticulars: '',
    husbandOccupationPreviousIllness: '',

    // Nominee Details (18)
    nomineeNameAge: '',
    nomineeRelationship: '',
    nomineeAddress: '',

    // Physical Details (19, 21, 22)
    heightCm: '',
    weightKgs: '',
    chestExIn: '',
    abdomen: '',
    identificationMarkA: '',
    identificationMarkB: '',
    appointeeName: '',
    appointeeAge: '',
    appointeeRelationship: '',

    // SSS Paying Authority (23)
    sssPayingAuthority: '',

    // Specimen Signature
    specimenSignature: '',
    signatureDate: new Date().toISOString().split('T')[0],
    declarationAgreed: true,
  });

  // Policy Details Table Rows
  const [policyRows, setPolicyRows] = useState([
    { planTerm: '', premium: '', sumAssd: '', mode: 'Yearly' },
  ]);

  // Family History Rows (Fixed members from image)
  const [familyHistory, setFamilyHistory] = useState([
    { member: "Father's Age", prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
    { member: "Mother's Age", prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
    { member: "Brother's Age", prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
    { member: "Sister's Age", prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
    { member: "Husband/Wife's Age", prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
    { member: 'Children Age', prAge: '', stateOfHealth: '', ageAtDeath: '', causeOfDeath: '' },
  ]);

  // Previous Policy Particulars Table (20)
  const [previousPolicies, setPreviousPolicies] = useState([
    {
      noOfPolicy: '',
      dateOfCommence: '',
      sumAssured: '',
      planTerm: '',
      withOrWithout: 'With Profits',
      modelOrNonMedl: 'Non-Medical',
      servicingBrRemarks: '',
    },
  ]);

  // Validation & Submission Modal States
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiSubmitStatus, setApiSubmitStatus] = useState(null); // 'success' | 'offline' | 'error' | null

  // Auto calculate age if DOB changes
  const handleDobChange = (e) => {
    const dobValue = e.target.value;
    let computedAge = formData.age;
    if (dobValue) {
      const birthDate = new Date(dobValue);
      const today = new Date();
      let ageYears = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        ageYears--;
      }
      if (ageYears >= 0 && ageYears < 120) {
        computedAge = ageYears.toString();
      }
    }
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: dobValue,
      age: computedAge,
    }));
    if (errors.dateOfBirth) {
      setErrors((prev) => ({ ...prev, dateOfBirth: '' }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Policy Rows Handlers
  const handlePolicyRowChange = (index, field, value) => {
    const updated = [...policyRows];
    updated[index][field] = value;
    setPolicyRows(updated);
  };

  const addPolicyRow = () => {
    setPolicyRows([...policyRows, { planTerm: '', premium: '', sumAssd: '', mode: 'Yearly' }]);
  };

  const removePolicyRow = (index) => {
    if (policyRows.length > 1) {
      setPolicyRows(policyRows.filter((_, i) => i !== index));
    }
  };

  // Family History Handlers
  const handleFamilyChange = (index, field, value) => {
    const updated = [...familyHistory];
    updated[index][field] = value;
    setFamilyHistory(updated);
  };

  // Previous Policies Handlers
  const handlePreviousPolicyChange = (index, field, value) => {
    const updated = [...previousPolicies];
    updated[index][field] = value;
    setPreviousPolicies(updated);
  };

  const addPreviousPolicyRow = () => {
    setPreviousPolicies([
      ...previousPolicies,
      {
        noOfPolicy: '',
        dateOfCommence: '',
        sumAssured: '',
        planTerm: '',
        withOrWithout: 'With Profits',
        modelOrNonMedl: 'Non-Medical',
        servicingBrRemarks: '',
      },
    ]);
  };

  const removePreviousPolicyRow = (index) => {
    if (previousPolicies.length > 1) {
      setPreviousPolicies(previousPolicies.filter((_, i) => i !== index));
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.residenceAddress.trim()) newErrors.residenceAddress = 'Residence Address is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of Birth is required';
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Valid E-Mail is required';
    }
    if (!formData.telResidence.trim() && !formData.telOffice.trim()) {
      newErrors.telResidence = 'Telephone / Contact number is required';
    }
    if (!formData.specimenSignature.trim()) {
      newErrors.specimenSignature = 'Specimen Signature of the proposer is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorEl = document.querySelector('.has-error');
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setApiSubmitStatus(null);

    const payload = {
      _subject: `LIC Bio-Data Customer Form - ${formData.name}`,
      _template: "table",
      _captcha: "false",
      "Proposer Name": formData.name,
      "Father Name": formData.fatherName || '-',
      "Mother Name": formData.motherName || '-',
      "Residence Address": formData.residenceAddress,
      "Office Address": formData.officeAddress || '-',
      "Occupation": formData.occupation || '-',
      "Monthly Income": formData.monthlyIncome || '-',
      "Department": formData.department || '-',
      "PF/Token/Batch No": formData.pfTokenBatchNo || '-',
      "Length of Service": formData.lengthOfService || '-',
      "Date of Birth": formData.dateOfBirth,
      "Place of Birth": formData.placeOfBirth || '-',
      "Age": formData.age || '-',
      "Educational Qualification": formData.educationalQualification || '-',
      "Deformity if any": formData.deformityIfAny || '-',
      "Previous illness if any": formData.previousIllnessIfAny || '-',
      "Agent Name": formData.agentName,
      "Agent Code": formData.code,
      "Policy No": formData.policyNo || '-',
      "Bank Account Type": formData.accountType,
      "Bank Account No": formData.accountNo || '-',
      "Bank 9 Digit MCIR": formData.mcir9Digit || '-',
      "Bank IFS Code": formData.ifsCode || '-',
      "Bank Name": formData.bankName || '-',
      "Bank Address": formData.nameAndAddressOfBank || '-',
      "Office Phone": formData.telOffice || '-',
      "Residence Phone": formData.telResidence || '-',
      "PAN Card No": formData.panCardNo || '-',
      "Aadhar No": formData.aadharNo || '-',
      "E-Mail": formData.email,
      "Nominee Name & Age": formData.nomineeNameAge || '-',
      "Nominee Relationship": formData.nomineeRelationship || '-',
      "Nominee Address": formData.nomineeAddress || '-',
      "Appointee (If Minor)": formData.appointeeName || '-',
      "Height (cm)": formData.heightCm || '-',
      "Weight (Kgs)": formData.weightKgs || '-',
      "Chest (Ex / in)": formData.chestExIn || '-',
      "Abdomen": formData.abdomen || '-',
      "Identification Marks": `(a) ${formData.identificationMarkA || '-'} | (b) ${formData.identificationMarkB || '-'}`,
      "Lady Life Last Menstruation": formData.dtLastMenstruation || '-',
      "Lady Life Last Delivery": formData.dtLastDelivery || '-',
      "Lady Life Husband Name": formData.husbandName || '-',
      "Lady Life Husband Monthly Income": formData.husbandMonthlyIncome || '-',
      "Lady Life Husband Insurance": formData.husbandInsuranceParticulars || '-',
      "Lady Life Husband Occupation/Illness": formData.husbandOccupationPreviousIllness || '-',
      "Policy Details": policyRows
        .filter(p => p.planTerm || p.premium || p.sumAssd)
        .map(p => `Plan/Term: ${p.planTerm || '-'}, Premium: ${p.premium || '-'}, Sum: ${p.sumAssd || '-'}, Mode: ${p.mode}`)
        .join('; ') || '-',
      "Family History": familyHistory
        .filter(f => f.prAge || f.stateOfHealth || f.ageAtDeath || f.causeOfDeath)
        .map(f => `${f.member}: Age ${f.prAge || '-'}, Health ${f.stateOfHealth || '-'}, Age at death ${f.ageAtDeath || '-'}, Cause ${f.causeOfDeath || '-'}`)
        .join('; ') || '-',
      "Previous Policy Details": previousPolicies
        .filter(p => p.noOfPolicy || p.sumAssured)
        .map(p => `Policy: ${p.noOfPolicy}, Sum: ${p.sumAssured}, Plan: ${p.planTerm}`)
        .join('; ') || '-',
      "SSS Paying Authority": formData.sssPayingAuthority || '-',
      "Specimen Signature": formData.specimenSignature,
    };

    try {
      const response = await fetch("https://formsubmit.co/ajax/vidhyashiva2027@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setApiSubmitStatus('success');
      } else {
        setApiSubmitStatus('error');
      }
    } catch (err) {
      console.warn("API Submission note:", err);
      setApiSubmitStatus('offline');
    } finally {
      setIsSubmitting(false);
      setShowSuccessModal(true);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to clear all form fields?')) {
      setFormData({
        name: '',
        fatherName: '',
        motherName: '',
        residenceAddress: '',
        officeAddress: '',
        occupation: '',
        monthlyIncome: '',
        department: '',
        pfTokenBatchNo: '',
        lengthOfService: '',
        dateOfBirth: '',
        placeOfBirth: '',
        age: '',
        educationalQualification: '',
        deformityIfAny: '',
        previousIllnessIfAny: '',
        agentName: 'Meenakshi Sundaram S',
        code: '0543571A',
        policyNo: '',
        accountType: 'SB A/c',
        accountNo: '',
        mcir9Digit: '',
        ifsCode: '',
        bankName: '',
        nameAndAddressOfBank: '',
        telOffice: '',
        telResidence: '',
        panCardNo: '',
        aadharNo: '',
        email: '',
        dtLastMenstruation: '',
        dtLastDelivery: '',
        husbandName: '',
        husbandMonthlyIncome: '',
        husbandInsuranceParticulars: '',
        husbandOccupationPreviousIllness: '',
        nomineeNameAge: '',
        nomineeRelationship: '',
        nomineeAddress: '',
        heightCm: '',
        weightKgs: '',
        chestExIn: '',
        abdomen: '',
        identificationMarkA: '',
        identificationMarkB: '',
        appointeeName: '',
        appointeeAge: '',
        appointeeRelationship: '',
        sssPayingAuthority: '',
        specimenSignature: '',
        signatureDate: new Date().toISOString().split('T')[0],
        declarationAgreed: true,
      });
      setErrors({});
    }
  };

  // Clean summary of customer details for copy/email
  const getFormattedSummaryText = () => {
    return `Customer Name: ${formData.name || '-'}
Date of Birth: ${formData.dateOfBirth || '-'} (Age: ${formData.age || '-'})
Email: ${formData.email || '-'}
Phone: ${formData.telResidence || formData.telOffice || '-'}
Address: ${formData.residenceAddress || '-'}
Occupation: ${formData.occupation || '-'} (${formData.monthlyIncome || '-'})
Agent: ${formData.agentName || '-'} (${formData.code || '-'})
Bank: ${formData.bankName || '-'} (A/C: ${formData.accountNo || '-'}, IFSC: ${formData.ifsCode || '-'})
Nominee: ${formData.nomineeNameAge || '-'} (${formData.nomineeRelationship || '-'})
Specimen Signature: ${formData.specimenSignature || '-'}`;
  };

  const handleCopyDetails = () => {
    navigator.clipboard.writeText(getFormattedSummaryText());
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const targetEmail = 'vidhyashiva2027@gmail.com';
  const mailtoSubject = encodeURIComponent(`LIC Bio-Data Customer Form - ${formData.name || 'New Submission'}`);
  const mailtoBody = encodeURIComponent(getFormattedSummaryText());
  const mailtoLink = `mailto:${targetEmail}?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <div className="customer-form-page">
      {/* Top Navbar */}
      <Navbar isVisible={true} currentPage="customer-form" onNavigate={onNavigate} />

      <main className="customer-form-container">
        {/* Top Controls Bar */}
        <div className="form-top-bar">
          <button
            type="button"
            className="btn-back-home"
            onClick={() => onNavigate && onNavigate('home')}
          >
            <ArrowLeft size={16} />
            <span>Back to Home</span>
          </button>

          <div className="form-badge-pill">
            <FileText size={15} />
            <span>LIC OF INDIA BIO-DATA FORM</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="lic-form-card">
          {/* Header Banner matching LIC format */}
          <header className="lic-official-header">
            <div className="lic-header-content">
              <div className="lic-brand-mark">
                <div className="lic-emblem-badge" aria-label="LIC Emblem">
                  LIC
                </div>
                <div className="lic-title-area">
                  <h2>भारतीय जीवन बीमा निगम / LIFE INSURANCE CORPORATION OF INDIA</h2>
                  <h1>LIC OF INDIA</h1>
                  <div className="lic-biodata-sub">BIO-DATA</div>
                </div>
              </div>

              <div className="lic-header-meta">
                <div className="meta-agency">Meena Insurance & Investments</div>
                <div className="meta-email">Recipient: vidhyashiva2027@gmail.com</div>
              </div>
            </div>
          </header>

          {/* Form Notice */}
          <div className="form-notice-bar">
            <span>
              Fill in all the required customer details. Submissions will be compiled and sent to{' '}
              <strong>vidhyashiva2027@gmail.com</strong>.
            </span>
            <button
              type="button"
              className="btn-lic-reset"
              onClick={handleReset}
              style={{ padding: '4px 8px', fontSize: '13px' }}
            >
              Clear Form
            </button>
          </div>

          {/* Form Form Tag */}
          <form onSubmit={handleSubmit} className="lic-form-body" noValidate>
            {/* Top Split Layout: Basic Info (Left) vs Agent / Bank / Telephone (Right) */}
            <div className="grid-two-columns-main">
              {/* LEFT COLUMN: A. Basic Information (1 to 16) */}
              <div className="form-column-box">
                <div className="form-section-title">
                  <span>A. Basic Information</span>
                </div>

                {/* 1. Name */}
                <div className="form-row-inline">
                  <label htmlFor="field-name" className="form-label-inline">
                    1. Name <span className="field-required">*</span>
                  </label>
                  <input
                    id="field-name"
                    type="text"
                    className={`form-input-control ${errors.name ? 'has-error' : ''}`}
                    placeholder="Full Proposer Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>

                {/* 2. Father Name */}
                <div className="form-row-inline">
                  <label htmlFor="field-father" className="form-label-inline">
                    2. Father Name
                  </label>
                  <input
                    id="field-father"
                    type="text"
                    className="form-input-control"
                    placeholder="Father's Full Name"
                    value={formData.fatherName}
                    onChange={(e) => handleInputChange('fatherName', e.target.value)}
                  />
                </div>

                {/* 3. Mother Name */}
                <div className="form-row-inline">
                  <label htmlFor="field-mother" className="form-label-inline">
                    3. Mother Name
                  </label>
                  <input
                    id="field-mother"
                    type="text"
                    className="form-input-control"
                    placeholder="Mother's Full Name"
                    value={formData.motherName}
                    onChange={(e) => handleInputChange('motherName', e.target.value)}
                  />
                </div>

                {/* 4. Residence Address */}
                <div className="form-row-inline form-row-textarea">
                  <label htmlFor="field-residence-addr" className="form-label-inline">
                    4. Residence Address <span className="field-required">*</span>
                  </label>
                  <textarea
                    id="field-residence-addr"
                    rows="2"
                    className={`form-input-control ${errors.residenceAddress ? 'has-error' : ''}`}
                    placeholder="Permanent / Residential Address with PIN"
                    value={formData.residenceAddress}
                    onChange={(e) => handleInputChange('residenceAddress', e.target.value)}
                    required
                  ></textarea>
                </div>

                {/* 5. Office Address */}
                <div className="form-row-inline form-row-textarea">
                  <label htmlFor="field-office-addr" className="form-label-inline">
                    5. Office Address
                  </label>
                  <textarea
                    id="field-office-addr"
                    rows="2"
                    className="form-input-control"
                    placeholder="Workplace / Office Address"
                    value={formData.officeAddress}
                    onChange={(e) => handleInputChange('officeAddress', e.target.value)}
                  ></textarea>
                </div>

                {/* 6. Occupation */}
                <div className="form-row-inline">
                  <label htmlFor="field-occupation" className="form-label-inline">
                    6. Occupation
                  </label>
                  <input
                    id="field-occupation"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. Salaried / Business / Professional"
                    value={formData.occupation}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                  />
                </div>

                {/* 7. Monthly Income */}
                <div className="form-row-inline">
                  <label htmlFor="field-income" className="form-label-inline">
                    7. Monthly Income
                  </label>
                  <input
                    id="field-income"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. ₹ 50,000"
                    value={formData.monthlyIncome}
                    onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                  />
                </div>

                {/* 8. Department */}
                <div className="form-row-inline">
                  <label htmlFor="field-department" className="form-label-inline">
                    8. Department
                  </label>
                  <input
                    id="field-department"
                    type="text"
                    className="form-input-control"
                    placeholder="Department / Branch"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                  />
                </div>

                {/* 9. PF/Token No./Batch No. */}
                <div className="form-row-inline">
                  <label htmlFor="field-pf" className="form-label-inline">
                    9. PF/Token No./Batch No.
                  </label>
                  <input
                    id="field-pf"
                    type="text"
                    className="form-input-control"
                    placeholder="PF / Token / Employee No."
                    value={formData.pfTokenBatchNo}
                    onChange={(e) => handleInputChange('pfTokenBatchNo', e.target.value)}
                  />
                </div>

                {/* 10. Length of Service */}
                <div className="form-row-inline">
                  <label htmlFor="field-service-len" className="form-label-inline">
                    10. Length of Service
                  </label>
                  <input
                    id="field-service-len"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. 5 Years"
                    value={formData.lengthOfService}
                    onChange={(e) => handleInputChange('lengthOfService', e.target.value)}
                  />
                </div>

                {/* 11. Date of Birth */}
                <div className="form-row-inline">
                  <label htmlFor="field-dob" className="form-label-inline">
                    11. Date of Birth <span className="field-required">*</span>
                  </label>
                  <input
                    id="field-dob"
                    type="date"
                    className={`form-input-control ${errors.dateOfBirth ? 'has-error' : ''}`}
                    value={formData.dateOfBirth}
                    onChange={handleDobChange}
                    required
                  />
                </div>

                {/* 12. Place of Birth */}
                <div className="form-row-inline">
                  <label htmlFor="field-place-birth" className="form-label-inline">
                    12. Place of Birth
                  </label>
                  <input
                    id="field-place-birth"
                    type="text"
                    className="form-input-control"
                    placeholder="City / Village / State"
                    value={formData.placeOfBirth}
                    onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                  />
                </div>

                {/* 13. Age */}
                <div className="form-row-inline">
                  <label htmlFor="field-age" className="form-label-inline">
                    13. Age
                  </label>
                  <input
                    id="field-age"
                    type="number"
                    className="form-input-control"
                    placeholder="Age (Auto-calculated from Date of Birth)"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                  />
                </div>

                {/* 14. Educational Qualification */}
                <div className="form-row-inline">
                  <label htmlFor="field-education" className="form-label-inline">
                    14. Educational Qualification
                  </label>
                  <input
                    id="field-education"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. Graduate / Post Graduate / SSLC"
                    value={formData.educationalQualification}
                    onChange={(e) => handleInputChange('educationalQualification', e.target.value)}
                  />
                </div>

                {/* 15. Deformity if any */}
                <div className="form-row-inline">
                  <label htmlFor="field-deformity" className="form-label-inline">
                    15. Deformity if any
                  </label>
                  <input
                    id="field-deformity"
                    type="text"
                    className="form-input-control"
                    placeholder="None or specify"
                    value={formData.deformityIfAny}
                    onChange={(e) => handleInputChange('deformityIfAny', e.target.value)}
                  />
                </div>

                {/* 16. Previous illness if any */}
                <div className="form-row-inline">
                  <label htmlFor="field-illness" className="form-label-inline">
                    16. Previous illness if any
                  </label>
                  <input
                    id="field-illness"
                    type="text"
                    className="form-input-control"
                    placeholder="None or specify medical history"
                    value={formData.previousIllnessIfAny}
                    onChange={(e) => handleInputChange('previousIllnessIfAny', e.target.value)}
                  />
                </div>
              </div>

              {/* RIGHT COLUMN: B. Agent & Policy, C. Bank Details, D. Telephone Nos. */}
              <div className="form-column-box">
                {/* B. Agent and Policy Information */}
                <div className="form-section-title">
                  <span>B. Agent and Policy Information</span>
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-agent-name" className="form-label-inline">
                    Agent Name
                  </label>
                  <input
                    id="field-agent-name"
                    type="text"
                    className="form-input-control"
                    value={formData.agentName}
                    onChange={(e) => handleInputChange('agentName', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-code" className="form-label-inline">
                    Code
                  </label>
                  <input
                    id="field-code"
                    type="text"
                    className="form-input-control"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-policy-no" className="form-label-inline">
                    Policy No.
                  </label>
                  <input
                    id="field-policy-no"
                    type="text"
                    className="form-input-control"
                    placeholder="Policy Number (if existing or proposal)"
                    value={formData.policyNo}
                    onChange={(e) => handleInputChange('policyNo', e.target.value)}
                  />
                </div>

                {/* Policy Details Table */}
                <div className="form-table-wrapper" style={{ marginTop: '14px' }}>
                  <table className="lic-table">
                    <thead>
                      <tr>
                        <th>PLAN-TERM</th>
                        <th>PREMIUM</th>
                        <th>SUM-ASSD</th>
                        <th>MODE</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {policyRows.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="e.g. 914-25"
                              value={row.planTerm}
                              onChange={(e) => handlePolicyRowChange(idx, 'planTerm', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="₹ Amount"
                              value={row.premium}
                              onChange={(e) => handlePolicyRowChange(idx, 'premium', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="₹ Sum Assured"
                              value={row.sumAssd}
                              onChange={(e) => handlePolicyRowChange(idx, 'sumAssd', e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              className="table-input-cell"
                              value={row.mode}
                              onChange={(e) => handlePolicyRowChange(idx, 'mode', e.target.value)}
                            >
                              <option value="Yearly">Yearly</option>
                              <option value="Half-Yearly">Half-Yearly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Monthly (NACH)">Monthly (NACH)</option>
                              <option value="SSS">SSS</option>
                            </select>
                          </td>
                          <td>
                            {policyRows.length > 1 && (
                              <button
                                type="button"
                                className="btn-delete-row"
                                onClick={() => removePolicyRow(idx)}
                                title="Delete row"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button type="button" className="btn-add-table-row" onClick={addPolicyRow}>
                  <Plus size={14} />
                  <span>Add Policy Row</span>
                </button>

                {/* C. Bank Account Details */}
                <div className="boxed-sub-section">
                  <div className="boxed-sub-title">(Bank Account Details :)</div>

                  <div className="form-row-inline">
                    <label htmlFor="field-account-type" className="form-label-inline">
                      (a) Type of A/C. SB/C A/c
                    </label>
                    <select
                      id="field-account-type"
                      className="form-input-control"
                      value={formData.accountType}
                      onChange={(e) => handleInputChange('accountType', e.target.value)}
                    >
                      <option value="SB A/c">SB A/c (Savings)</option>
                      <option value="Current A/c">Current A/c</option>
                    </select>
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-account-no" className="form-label-inline">
                      (b) Your Account No.
                    </label>
                    <input
                      id="field-account-no"
                      type="text"
                      className="form-input-control"
                      placeholder="Account Number"
                      value={formData.accountNo}
                      onChange={(e) => handleInputChange('accountNo', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-mcir" className="form-label-inline">
                      (c) 9 Digit MCIR
                    </label>
                    <input
                      id="field-mcir"
                      type="text"
                      maxLength={9}
                      className="form-input-control"
                      placeholder="9-digit MICR code"
                      value={formData.mcir9Digit}
                      onChange={(e) => handleInputChange('mcir9Digit', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-ifs" className="form-label-inline">
                      (d) IFS Code :
                    </label>
                    <input
                      id="field-ifs"
                      type="text"
                      className="form-input-control"
                      placeholder="IFSC (e.g. SBIN0001234)"
                      value={formData.ifsCode}
                      onChange={(e) => handleInputChange('ifsCode', e.target.value.toUpperCase())}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-bank-name" className="form-label-inline">
                      (e) Bank Name :
                    </label>
                    <input
                      id="field-bank-name"
                      type="text"
                      className="form-input-control"
                      placeholder="Name of Bank"
                      value={formData.bankName}
                      onChange={(e) => handleInputChange('bankName', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-bank-address" className="form-label-inline">
                      (f) Name & Address of
                    </label>
                    <input
                      id="field-bank-address"
                      type="text"
                      className="form-input-control"
                      placeholder="Branch name & address"
                      value={formData.nameAndAddressOfBank}
                      onChange={(e) => handleInputChange('nameAndAddressOfBank', e.target.value)}
                    />
                  </div>
                </div>

                {/* D. You Telephone Nos. (With STD Code) */}
                <div className="boxed-sub-section">
                  <div className="boxed-sub-title">You Telephone Nos. (With STD Code)</div>

                  <div className="form-row-inline">
                    <label htmlFor="field-tel-office" className="form-label-inline">
                      a. Office :
                    </label>
                    <input
                      id="field-tel-office"
                      type="tel"
                      className="form-input-control"
                      placeholder="Office Telephone / STD"
                      value={formData.telOffice}
                      onChange={(e) => handleInputChange('telOffice', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-tel-residence" className="form-label-inline">
                      b. Residence : <span className="field-required">*</span>
                    </label>
                    <input
                      id="field-tel-residence"
                      type="tel"
                      className={`form-input-control ${errors.telResidence ? 'has-error' : ''}`}
                      placeholder="Mobile / Residence Phone"
                      value={formData.telResidence}
                      onChange={(e) => handleInputChange('telResidence', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-pan" className="form-label-inline">
                      c. Pan Card No. :
                    </label>
                    <input
                      id="field-pan"
                      type="text"
                      maxLength={10}
                      className="form-input-control"
                      placeholder="10-digit PAN (e.g. ABCDE1234F)"
                      value={formData.panCardNo}
                      onChange={(e) => handleInputChange('panCardNo', e.target.value.toUpperCase())}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-aadhar" className="form-label-inline">
                      d. Aadhar No. :
                    </label>
                    <input
                      id="field-aadhar"
                      type="text"
                      maxLength={14}
                      className="form-input-control"
                      placeholder="12-digit Aadhaar Number"
                      value={formData.aadharNo}
                      onChange={(e) => handleInputChange('aadharNo', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-email" className="form-label-inline">
                      e. E-Mail : <span className="field-required">*</span>
                    </label>
                    <input
                      id="field-email"
                      type="email"
                      className={`form-input-control ${errors.email ? 'has-error' : ''}`}
                      placeholder="proposer@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Section: E. Family History vs F. FOR LADY LIFE */}
            <div className="grid-family-lady">
              {/* E. Family History */}
              <div className="form-column-box">
                <div className="form-section-title">
                  <span>16. Family History :</span>
                </div>

                <div className="form-table-wrapper">
                  <table className="lic-table">
                    <thead>
                      <tr>
                        <th style={{ width: '130px' }}>Member</th>
                        <th style={{ width: '70px' }}>Pr. Age</th>
                        <th>State of Health</th>
                        <th style={{ width: '110px' }}>If dead, age at the death</th>
                        <th>Cause of death</th>
                      </tr>
                    </thead>
                    <tbody>
                      {familyHistory.map((row, idx) => (
                        <tr key={idx}>
                          <td className="table-row-label">{row.member} :</td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="Age"
                              value={row.prAge}
                              onChange={(e) => handleFamilyChange(idx, 'prAge', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="Good / Normal"
                              value={row.stateOfHealth}
                              onChange={(e) => handleFamilyChange(idx, 'stateOfHealth', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="Age at death"
                              value={row.ageAtDeath}
                              onChange={(e) => handleFamilyChange(idx, 'ageAtDeath', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="table-input-cell"
                              placeholder="Cause"
                              value={row.causeOfDeath}
                              onChange={(e) => handleFamilyChange(idx, 'causeOfDeath', e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* F. FOR LADY LIFE */}
              <div className="form-column-box">
                <div className="form-section-title accent-warning">
                  <span>17. FOR LADY LIFE :</span>
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-menstruation" className="form-label-inline">
                    1. Dt. of Last Menstruation :
                  </label>
                  <input
                    id="field-menstruation"
                    type="date"
                    className="form-input-control"
                    value={formData.dtLastMenstruation}
                    onChange={(e) => handleInputChange('dtLastMenstruation', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-delivery" className="form-label-inline">
                    2. Dt. of Last Delivery :
                  </label>
                  <input
                    id="field-delivery"
                    type="date"
                    className="form-input-control"
                    value={formData.dtLastDelivery}
                    onChange={(e) => handleInputChange('dtLastDelivery', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-husband-name" className="form-label-inline">
                    3. Husband Name :
                  </label>
                  <input
                    id="field-husband-name"
                    type="text"
                    className="form-input-control"
                    placeholder="Husband Full Name"
                    value={formData.husbandName}
                    onChange={(e) => handleInputChange('husbandName', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-husband-income" className="form-label-inline">
                    4. Husband Monthly Income :
                  </label>
                  <input
                    id="field-husband-income"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. ₹ 60,000"
                    value={formData.husbandMonthlyIncome}
                    onChange={(e) => handleInputChange('husbandMonthlyIncome', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-husband-insurance" className="form-label-inline">
                    5. Husband Insurance Particulars :
                  </label>
                  <input
                    id="field-husband-insurance"
                    type="text"
                    className="form-input-control"
                    placeholder="Husband's Policy No. / Sum Assured"
                    value={formData.husbandInsuranceParticulars}
                    onChange={(e) => handleInputChange('husbandInsuranceParticulars', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-husband-occupation" className="form-label-inline">
                    6. Husband Occupation Previous illness
                  </label>
                  <input
                    id="field-husband-occupation"
                    type="text"
                    className="form-input-control"
                    placeholder="Husband's Occupation / illness details"
                    value={formData.husbandOccupationPreviousIllness}
                    onChange={(e) =>
                      handleInputChange('husbandOccupationPreviousIllness', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Lower Grid: G. Nominee Details & H. Physical Details */}
            <div className="grid-nominee-physical">
              {/* G. Nominee Details */}
              <div className="form-column-box">
                <div className="form-section-title">
                  <span>G. Nominee Details</span>
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-nominee-name" className="form-label-inline">
                    18. Nominee's Name & Age
                  </label>
                  <input
                    id="field-nominee-name"
                    type="text"
                    className="form-input-control"
                    placeholder="Nominee Name & Age"
                    value={formData.nomineeNameAge}
                    onChange={(e) => handleInputChange('nomineeNameAge', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-nominee-rel" className="form-label-inline">
                    Relationship :
                  </label>
                  <input
                    id="field-nominee-rel"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. Spouse / Son / Daughter / Mother"
                    value={formData.nomineeRelationship}
                    onChange={(e) => handleInputChange('nomineeRelationship', e.target.value)}
                  />
                </div>

                <div className="form-row-inline form-row-textarea">
                  <label htmlFor="field-nominee-address" className="form-label-inline">
                    Address :
                  </label>
                  <textarea
                    id="field-nominee-address"
                    rows="2"
                    className="form-input-control"
                    placeholder="Nominee Residential Address"
                    value={formData.nomineeAddress}
                    onChange={(e) => handleInputChange('nomineeAddress', e.target.value)}
                  ></textarea>
                </div>

                {/* 22. If nominee Minor */}
                <div className="boxed-sub-section" style={{ marginTop: '16px' }}>
                  <div className="boxed-sub-title">22. If nominee Minor :</div>

                  <div className="form-row-inline">
                    <label htmlFor="field-appointee-name" className="form-label-inline">
                      Appointee Name :
                    </label>
                    <input
                      id="field-appointee-name"
                      type="text"
                      className="form-input-control"
                      placeholder="Appointee Name"
                      value={formData.appointeeName}
                      onChange={(e) => handleInputChange('appointeeName', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-appointee-age" className="form-label-inline">
                      Age :
                    </label>
                    <input
                      id="field-appointee-age"
                      type="number"
                      className="form-input-control"
                      placeholder="Age"
                      value={formData.appointeeAge}
                      onChange={(e) => handleInputChange('appointeeAge', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-appointee-rel" className="form-label-inline">
                      Relationship :
                    </label>
                    <input
                      id="field-appointee-rel"
                      type="text"
                      className="form-input-control"
                      placeholder="Relationship with Minor"
                      value={formData.appointeeRelationship}
                      onChange={(e) => handleInputChange('appointeeRelationship', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* H. Physical Details */}
              <div className="form-column-box">
                <div className="form-section-title">
                  <span>H. Physical Details</span>
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-height" className="form-label-inline">
                    19. Height (in cm) :
                  </label>
                  <input
                    id="field-height"
                    type="number"
                    className="form-input-control"
                    placeholder="e.g. 172"
                    value={formData.heightCm}
                    onChange={(e) => handleInputChange('heightCm', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-weight" className="form-label-inline">
                    (Weight in Kgs.) :
                  </label>
                  <input
                    id="field-weight"
                    type="number"
                    className="form-input-control"
                    placeholder="e.g. 68"
                    value={formData.weightKgs}
                    onChange={(e) => handleInputChange('weightKgs', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-chest" className="form-label-inline">
                    Chest (Ex / in) :
                  </label>
                  <input
                    id="field-chest"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. 38 / 40 inches"
                    value={formData.chestExIn}
                    onChange={(e) => handleInputChange('chestExIn', e.target.value)}
                  />
                </div>

                <div className="form-row-inline">
                  <label htmlFor="field-abdomen" className="form-label-inline">
                    Abdomen :
                  </label>
                  <input
                    id="field-abdomen"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. 34 inches"
                    value={formData.abdomen}
                    onChange={(e) => handleInputChange('abdomen', e.target.value)}
                  />
                </div>

                {/* 21. Indentification Marks */}
                <div className="boxed-sub-section" style={{ marginTop: '16px' }}>
                  <div className="boxed-sub-title">21. Indentification Marks :</div>

                  <div className="form-row-inline">
                    <label htmlFor="field-id-mark-a" className="form-label-inline">
                      (a)
                    </label>
                    <input
                      id="field-id-mark-a"
                      type="text"
                      className="form-input-control"
                      placeholder="Mole or scar mark"
                      value={formData.identificationMarkA}
                      onChange={(e) => handleInputChange('identificationMarkA', e.target.value)}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label htmlFor="field-id-mark-b" className="form-label-inline">
                      (b)
                    </label>
                    <input
                      id="field-id-mark-b"
                      type="text"
                      className="form-input-control"
                      placeholder="Second identification mark"
                      value={formData.identificationMarkB}
                      onChange={(e) => handleInputChange('identificationMarkB', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* I. Previous Policy Particulars Table (20) */}
            <div className="form-column-box" style={{ marginBottom: '32px' }}>
              <div className="form-section-title">
                <span>20. Previous Policy Particulars</span>
              </div>

              <div className="form-table-wrapper">
                <table className="lic-table">
                  <thead>
                    <tr>
                      <th>No. of Policy</th>
                      <th>Date of Commence</th>
                      <th>Sum Assured</th>
                      <th>Plan Term</th>
                      <th>With of Without</th>
                      <th>Model or Non Medl.</th>
                      <th>Servicing Br. & Remarks</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {previousPolicies.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            type="text"
                            className="table-input-cell"
                            placeholder="Policy Number"
                            value={row.noOfPolicy}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'noOfPolicy', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="table-input-cell"
                            value={row.dateOfCommence}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'dateOfCommence', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-input-cell"
                            placeholder="₹ Sum Assured"
                            value={row.sumAssured}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'sumAssured', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-input-cell"
                            placeholder="Plan / Term"
                            value={row.planTerm}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'planTerm', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <select
                            className="table-input-cell"
                            value={row.withOrWithout}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'withOrWithout', e.target.value)
                            }
                          >
                            <option value="With Profits">With Profits</option>
                            <option value="Without Profits">Without Profits</option>
                          </select>
                        </td>
                        <td>
                          <select
                            className="table-input-cell"
                            value={row.modelOrNonMedl}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'modelOrNonMedl', e.target.value)
                            }
                          >
                            <option value="Non-Medical">Non-Medical</option>
                            <option value="Medical">Medical</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-input-cell"
                            placeholder="Branch & Remarks"
                            value={row.servicingBrRemarks}
                            onChange={(e) =>
                              handlePreviousPolicyChange(idx, 'servicingBrRemarks', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          {previousPolicies.length > 1 && (
                            <button
                              type="button"
                              className="btn-delete-row"
                              onClick={() => removePreviousPolicyRow(idx)}
                              title="Delete row"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn-add-table-row"
                onClick={addPreviousPolicyRow}
              >
                <Plus size={14} />
                <span>Add Previous Policy Row</span>
              </button>
            </div>

            {/* J. SSS Paying Authority (23) */}
            <div className="sss-authority-card">
              <div className="form-row-inline form-row-sss">
                <label htmlFor="field-sss-authority" className="form-label-inline">
                  23. For SSS : PAYING AUTHORITY :
                </label>
                <input
                  id="field-sss-authority"
                  type="text"
                  className="form-input-control"
                  placeholder="Employer / Paying Authority name & code (if SSS policy)"
                  value={formData.sssPayingAuthority}
                  onChange={(e) => handleInputChange('sssPayingAuthority', e.target.value)}
                />
              </div>
            </div>

            {/* K. Signature Section */}
            <div className="signature-card">
              <div className="signature-card-header">
                <span className="signature-title">
                  SPECIMEN SIGNATURE OF THE PROPOSER
                </span>
                <span style={{ fontSize: '13px', color: '#64748B' }}>
                  Legal Declaration & Specimen Signature
                </span>
              </div>

              <p className="signature-declaration">
                I hereby declare that the foregoing statements and answers have been given by me after
                fully understanding the questions and the same are true and complete in every
                particular and that I have not withheld or concealed any information.
              </p>

              <div className="signature-inputs-grid">
                <div>
                  <label htmlFor="field-specimen-sign" className="form-label-inline">
                    Specimen Signature (Type Full Legal Name) <span className="field-required">*</span>
                  </label>
                  <input
                    id="field-specimen-sign"
                    type="text"
                    className={`form-input-control ${errors.specimenSignature ? 'has-error' : ''}`}
                    placeholder="Enter full name as legal specimen signature"
                    value={formData.specimenSignature}
                    onChange={(e) => handleInputChange('specimenSignature', e.target.value)}
                    required
                  />
                  {errors.specimenSignature && (
                    <span style={{ color: '#DC2626', fontSize: '12px' }}>
                      {errors.specimenSignature}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="field-sign-date" className="form-label-inline">
                    Date
                  </label>
                  <input
                    id="field-sign-date"
                    type="date"
                    className="form-input-control"
                    value={formData.signatureDate}
                    onChange={(e) => handleInputChange('signatureDate', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="field-place" className="form-label-inline">
                    Place
                  </label>
                  <input
                    id="field-place"
                    type="text"
                    className="form-input-control"
                    placeholder="e.g. Chennai"
                    value={formData.placeOfBirth || ''}
                    onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Error Summary if any */}
            {Object.keys(errors).length > 0 && (
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '10px',
                  padding: '14px 18px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#B91C1C',
                }}
              >
                <AlertCircle size={20} />
                <span>
                  Please fill all mandatory fields: Name, Residence Address, Date of Birth, Contact
                  Phone, E-Mail, and Specimen Signature.
                </span>
              </div>
            )}

            {/* Bottom Actions Bar */}
            <div className="form-footer-actions">
              <div className="form-action-left">
                <button
                  type="button"
                  className="btn-lic-secondary"
                  onClick={() => window.print()}
                >
                  <Printer size={16} />
                  <span>Print / Save PDF</span>
                </button>
              </div>

              <div className="form-action-right">
                <button type="button" className="btn-lic-reset" onClick={handleReset}>
                  Clear Form
                </button>

                <button type="submit" className="btn-lic-submit" disabled={isSubmitting}>
                  <span>{isSubmitting ? 'Submitting to vidhyashiva2027@gmail.com...' : 'Submit Customer Form'}</span>
                  <CheckCircle2 size={20} />
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Submission Success Modal with Full Customer Details */}
      {showSuccessModal && (
        <div
          className="submission-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target.classList.contains('submission-modal-backdrop')) {
              setShowSuccessModal(false);
            }
          }}
        >
          <div className="submission-modal-card">
            {/* Modal Header */}
            <div className="modal-header-success">
              <div className="modal-header-title">
                <div className="modal-check-icon">
                  <Check size={24} />
                </div>
                <div>
                  <h3>Customer Form Submitted Successfully!</h3>
                  <p>LIC OF INDIA BIO-DATA record is ready for review and email dispatch.</p>
                </div>
              </div>

              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowSuccessModal(false)}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Recipient Callout */}
            <div className="modal-email-callout">
              <div>
                Target Recipient: <strong>vidhyashiva2027@gmail.com</strong>
                {apiSubmitStatus === 'success' && (
                  <span style={{ marginLeft: '10px', color: '#047857', fontWeight: 600 }}>
                    ✓ Successfully delivered to email API!
                  </span>
                )}
                {apiSubmitStatus === 'offline' && (
                  <span style={{ marginLeft: '10px', color: '#B45309', fontWeight: 600 }}>
                    ℹ Details compiled. Click below to send via your email client.
                  </span>
                )}
              </div>
              <div>
                Proposer: <strong>{formData.name}</strong> ({formData.email})
              </div>
            </div>

            {/* Scrollable Customer Details Summary */}
            <div className="modal-content-scrollable">
              {/* Section A */}
              <div className="review-section-group">
                <div className="review-section-header">A. Basic Information</div>
                <div className="review-grid-data">
                  <div className="review-field-pair">
                    <span className="review-field-label">1. Name</span>
                    <span className="review-field-value">{formData.name || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">2. Father Name</span>
                    <span className="review-field-value">{formData.fatherName || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">3. Mother Name</span>
                    <span className="review-field-value">{formData.motherName || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">4. Residence Address</span>
                    <span className="review-field-value">{formData.residenceAddress || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">5. Office Address</span>
                    <span className="review-field-value">{formData.officeAddress || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">6. Occupation</span>
                    <span className="review-field-value">{formData.occupation || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">7. Monthly Income</span>
                    <span className="review-field-value">{formData.monthlyIncome || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">8. Department</span>
                    <span className="review-field-value">{formData.department || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">9. PF/Token No./Batch No.</span>
                    <span className="review-field-value">{formData.pfTokenBatchNo || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">10. Length of Service</span>
                    <span className="review-field-value">{formData.lengthOfService || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">11. Date of Birth</span>
                    <span className="review-field-value">{formData.dateOfBirth || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">12. Place of Birth</span>
                    <span className="review-field-value">{formData.placeOfBirth || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">13. Age</span>
                    <span className="review-field-value">{formData.age || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">14. Educational Qualification</span>
                    <span className="review-field-value">{formData.educationalQualification || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">15. Deformity if any</span>
                    <span className="review-field-value">{formData.deformityIfAny || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">16. Previous illness if any</span>
                    <span className="review-field-value">{formData.previousIllnessIfAny || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Section B & C & D */}
              <div className="review-section-group">
                <div className="review-section-header">B, C & D. Policy, Bank & Telephone Details</div>
                <div className="review-grid-data">
                  <div className="review-field-pair">
                    <span className="review-field-label">Agent Name</span>
                    <span className="review-field-value">{formData.agentName || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Code</span>
                    <span className="review-field-value">{formData.code || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Policy No.</span>
                    <span className="review-field-value">{formData.policyNo || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(a) Type of A/C. SB/C A/c</span>
                    <span className="review-field-value">{formData.accountType || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(b) Your Account No.</span>
                    <span className="review-field-value">{formData.accountNo || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(c) 9 Digit MCIR</span>
                    <span className="review-field-value">{formData.mcir9Digit || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(d) IFS Code</span>
                    <span className="review-field-value">{formData.ifsCode || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(e) Bank Name</span>
                    <span className="review-field-value">{formData.bankName || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(f) Name & Address of</span>
                    <span className="review-field-value">{formData.nameAndAddressOfBank || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">a. Office Phone</span>
                    <span className="review-field-value">{formData.telOffice || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">b. Residence Phone</span>
                    <span className="review-field-value">{formData.telResidence || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">c. Pan Card No.</span>
                    <span className="review-field-value">{formData.panCardNo || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">d. Aadhar No.</span>
                    <span className="review-field-value">{formData.aadharNo || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">e. E-Mail</span>
                    <span className="review-field-value">{formData.email || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Section G, H, J, K */}
              <div className="review-section-group">
                <div className="review-section-header">Nominee, Physical, SSS & Signature</div>
                <div className="review-grid-data">
                  <div className="review-field-pair">
                    <span className="review-field-label">18. Nominee's Name & Age</span>
                    <span className="review-field-value">{formData.nomineeNameAge || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Relationship</span>
                    <span className="review-field-value">{formData.nomineeRelationship || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Nominee Address</span>
                    <span className="review-field-value">{formData.nomineeAddress || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">19. Height (in cm)</span>
                    <span className="review-field-value">{formData.heightCm || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">(Weight in Kgs.)</span>
                    <span className="review-field-value">{formData.weightKgs || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Chest (Ex / in)</span>
                    <span className="review-field-value">{formData.chestExIn || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">Abdomen</span>
                    <span className="review-field-value">{formData.abdomen || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">21. Identification Marks</span>
                    <span className="review-field-value">
                      (a) {formData.identificationMarkA || '-'} | (b) {formData.identificationMarkB || '-'}
                    </span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">23. For SSS : PAYING AUTHORITY</span>
                    <span className="review-field-value">{formData.sssPayingAuthority || '-'}</span>
                  </div>
                  <div className="review-field-pair">
                    <span className="review-field-label">SPECIMEN SIGNATURE</span>
                    <span className="review-field-value">{formData.specimenSignature || '-'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="modal-footer-actions">
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <a
                  href={mailtoLink}
                  className="btn-send-email-direct"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Send size={16} />
                  <span>Send to vidhyashiva2027@gmail.com</span>
                </a>

                <button type="button" className="btn-copy-data" onClick={handleCopyDetails}>
                  {copied ? <Check size={16} color="#10B981" /> : <Copy size={16} />}
                  <span>{copied ? 'Details Copied!' : 'Copy Details'}</span>
                </button>
              </div>

              <button
                type="button"
                className="btn-lic-secondary"
                onClick={() => {
                  setShowSuccessModal(false);
                  onNavigate && onNavigate('home');
                }}
              >
                <span>Done / Back to Home</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default CustomerFormPage;
